import { put, takeLatest, all, call } from 'redux-saga/effects';

import { heliumReceive, requestAndSet } from 'utils/helpers';

import { agreementsActions } from '../slices/agreementsSlice';
import { Agreement, AgreementContent } from '../../types/agreementTypes';

interface AgreementApiResult {
  data?: Array<Record<string, unknown>>;
}

function* fetchAgreementsSaga(
  action: ReturnType<typeof agreementsActions.fetchAgreements>,
): Generator {
  const { orgId, status } = action.payload;
  yield requestAndSet('listAllDocument', [orgId], {
    queryStringParams: { status },
    createSetterAction: agreementsActions.setAgreements,
    loadingAction: (v: boolean) =>
      agreementsActions.setLoading({ key: 'fetchAgreementsLoading', value: v }),
  });
}

function* describeAgreementSaga(
  action: ReturnType<typeof agreementsActions.describeAgreement>,
): Generator {
  const { orgId, agreementId } = action.payload;
  yield requestAndSet('describeDocument', [orgId, agreementId], {
    createSetterAction: (res: unknown) => {
      const apiResult = res as AgreementApiResult;
      return agreementsActions.setSelectedAgreement(
        (apiResult.data?.[0] as Agreement | undefined) || null,
      );
    },
    loadingAction: (v: boolean) =>
      agreementsActions.setLoading({
        key: 'describeAgreementLoading',
        value: v,
      }),
  });
}

function* createAgreementSaga(
  action: ReturnType<typeof agreementsActions.createAgreement>,
): Generator {
  const { orgId, agreement, content } = action.payload;
  try {
    yield put(
      agreementsActions.setLoading({
        key: 'createAgreementLoading',
        value: true,
      }),
    );

    const result = (yield call(
      heliumReceive,
      'insertDocument',
      [orgId],
      agreement,
    )) as { data?: Array<{ id?: number }> } | undefined;

    if (result?.data?.[0]?.id) {
      const newId = result.data[0].id;

      if (content) {
        yield call(heliumReceive, 'updateDocumentContent', [orgId, newId], {
          content,
        });
      }

      yield put(agreementsActions.fetchAgreements({ orgId }));
    }
  } catch {
    // Error creating agreement
  } finally {
    yield put(
      agreementsActions.setLoading({
        key: 'createAgreementLoading',
        value: false,
      }),
    );
  }
}

function* updateAgreementSaga(
  action: ReturnType<typeof agreementsActions.updateAgreement>,
): Generator {
  const { orgId, agreementId, agreement } = action.payload;
  yield requestAndSet('updateDocument', [orgId, agreementId], {
    body: agreement,
    loadingAction: (v: boolean) =>
      agreementsActions.setLoading({ key: 'updateAgreementLoading', value: v }),
    *postSuccessAction() {
      yield put(agreementsActions.fetchAgreements({ orgId }));
      yield put(agreementsActions.describeAgreement({ orgId, agreementId }));
    },
  });
}

function* deleteAgreementSaga(
  action: ReturnType<typeof agreementsActions.deleteAgreement>,
): Generator {
  const { orgId, agreementId } = action.payload;
  yield requestAndSet('deleteDocument', [orgId, agreementId], {
    loadingAction: (v: boolean) =>
      agreementsActions.setLoading({ key: 'deleteAgreementLoading', value: v }),
    *postSuccessAction() {
      yield put(agreementsActions.fetchAgreements({ orgId }));
    },
  });
}

function* describeAgreementContentSaga(
  action: ReturnType<typeof agreementsActions.describeAgreementContent>,
): Generator {
  const { orgId, agreementId } = action.payload;
  yield requestAndSet('getDocumentContent', [orgId, agreementId], {
    createSetterAction: (res: unknown) => {
      const apiResult = res as AgreementApiResult;
      return agreementsActions.setSelectedDocumentContent(
        (apiResult.data?.[0] as AgreementContent | undefined) || null,
      );
    },
    loadingAction: (v: boolean) =>
      agreementsActions.setLoading({
        key: 'describeAgreementContentLoading',
        value: v,
      }),
  });
}

function* updateAgreementContentSaga(
  action: ReturnType<typeof agreementsActions.updateAgreementContent>,
): Generator {
  const { orgId, agreementId, content } = action.payload;
  yield requestAndSet('updateDocumentContent', [orgId, agreementId], {
    body: content,
    loadingAction: (v: boolean) =>
      agreementsActions.setLoading({
        key: 'updateAgreementContentLoading',
        value: v,
      }),
    *postSuccessAction() {
      yield put(
        agreementsActions.describeAgreementContent({ orgId, agreementId }),
      );
    },
  });
}

export default function* agreementsSaga(): Generator {
  yield all([
    takeLatest(agreementsActions.fetchAgreements.type, fetchAgreementsSaga),
    takeLatest(agreementsActions.describeAgreement.type, describeAgreementSaga),
    takeLatest(agreementsActions.createAgreement.type, createAgreementSaga),
    takeLatest(agreementsActions.updateAgreement.type, updateAgreementSaga),
    takeLatest(agreementsActions.deleteAgreement.type, deleteAgreementSaga),
    takeLatest(
      agreementsActions.describeAgreementContent.type,
      describeAgreementContentSaga,
    ),
    takeLatest(
      agreementsActions.updateAgreementContent.type,
      updateAgreementContentSaga,
    ),
  ]);
}
